/**
 * Carer guidance as ONE free-form text per goal (Ed, 2026-09-29: "make the
 * rendering problem largely theirs — type it wrong, it renders wrong").
 *
 * The author writes one markdown text; the planner's preview and the carer app
 * draw it the same way and never re-sort it. The rules, nothing else:
 *
 *   # Title               a fold (the first one opens by itself)
 *   # Level N · Name      a numbered level fold (N = 0..5)
 *   ## Label              a small-capitals label inside a fold
 *   1. Step               numbered steps
 *   - **N Name** — text   a score line with its level badge
 *   - Item                bullets
 *   > Note                the boxed note (a `>` blank line splits paragraphs)
 *   **bold**              bold, anywhere
 *
 * Anything else is a plain paragraph — never hidden, never dropped.
 *
 * Storage: `_carerGuidanceText` (string) on `_activityDefinition` (the
 * catalogue) and, as a whole-text per-plan override, on `_goal`. When a doc has
 * no text yet, `composeFromLevels` builds it on the fly from the legacy 5 level
 * texts (`documentation[].display` / `_goal._carerGuidance`), so nothing is
 * migrated in bulk. Mirrored in Dart (app.carer/lib/utils/guidance_text.dart);
 * both run architecture/fixtures/carer-guidance/cases.json.
 */

export type GuidanceBlock =
  | { kind: "label"; text: string }
  | { kind: "para"; text: string }
  | { kind: "steps"; items: string[] }
  | { kind: "bullets"; items: string[] }
  | { kind: "scale"; items: { n: number; name: string; text: string }[] }
  | { kind: "note"; paras: string[] };

export interface GuidanceFold {
  title: string;
  /** 0..5 when the title starts "Level N", else null. */
  level: number | null;
  blocks: GuidanceBlock[];
}

export interface GuidanceDoc {
  /** Anything written before the first `# ` line. */
  intro: GuidanceBlock[];
  folds: GuidanceFold[];
}

const FOLD = /^#\s+(.+)$/;
const LABEL = /^##\s+(.+)$/;
const LEVEL = /^Level\s+([0-5])(?![0-9])/i;
const SCALE = /^[-*]\s+\*\*([0-5])\s+(.+?)\*\*\s*[—–-]\s*(.*)$/;
const STEP = /^\d+[.)]\s+(.*)$/;
const BULLET = /^[-*]\s+(.*)$/;
const NOTE = /^>\s?(.*)$/;

type ListBlock = Extract<GuidanceBlock, { kind: "steps" | "bullets" | "scale" }>;

/** Parse one guidance text into intro + folds. Pure; never throws. */
export function parseGuidanceText(src: string): GuidanceDoc {
  const doc: GuidanceDoc = { intro: [], folds: [] };
  // Held on one object so TypeScript doesn't narrow them across the closures.
  const st = {
    blocks: doc.intro,
    para: [] as string[],
    list: null as ListBlock | null,
    note: null as string[][] | null,
  };

  const flushPara = () => {
    if (st.para.length) st.blocks.push({ kind: "para", text: st.para.join(" ") });
    st.para = [];
  };
  const flushList = () => {
    if (st.list) st.blocks.push(st.list);
    st.list = null;
  };
  const flushNote = () => {
    const paras = (st.note ?? []).map((p) => p.join(" ")).filter((p) => p !== "");
    if (paras.length) st.blocks.push({ kind: "note", paras });
    st.note = null;
  };
  const flushAll = () => {
    flushPara();
    flushList();
    flushNote();
  };
  /** The open list of this kind, starting a new one if needed. */
  const listOf = <K extends ListBlock["kind"]>(kind: K): Extract<ListBlock, { kind: K }> => {
    flushPara();
    if (st.list?.kind !== kind) {
      flushList();
      st.list = { kind, items: [] } as ListBlock;
    }
    return st.list as Extract<ListBlock, { kind: K }>;
  };

  for (const raw of src.replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.trim();
    let m: RegExpMatchArray | null;

    if ((m = line.match(NOTE))) {
      flushPara();
      flushList();
      const body = m[1].trim();
      st.note ??= [[]];
      if (body === "") st.note.push([]);
      else st.note[st.note.length - 1].push(body);
      continue;
    }
    flushNote();

    if (line === "") {
      flushPara();
      flushList();
    } else if ((m = line.match(FOLD))) {
      flushAll();
      const title = m[1].trim();
      const lv = title.match(LEVEL);
      const fold: GuidanceFold = { title, level: lv ? Number(lv[1]) : null, blocks: [] };
      doc.folds.push(fold);
      st.blocks = fold.blocks;
    } else if ((m = line.match(LABEL))) {
      flushAll();
      st.blocks.push({ kind: "label", text: m[1].trim() });
    } else if ((m = line.match(SCALE))) {
      listOf("scale").items.push({ n: Number(m[1]), name: m[2].trim(), text: m[3].trim() });
    } else if ((m = line.match(STEP))) {
      listOf("steps").items.push(m[1].trim());
    } else if ((m = line.match(BULLET))) {
      listOf("bullets").items.push(m[1].trim());
    } else if (st.list?.kind === "scale") {
      // A plain line straight after a list item (no blank line) continues it.
      const it = st.list.items[st.list.items.length - 1];
      it.text = it.text ? `${it.text} ${line}` : line;
    } else if (st.list) {
      const items = st.list.items;
      items[items.length - 1] = `${items[items.length - 1]} ${line}`;
    } else {
      st.para.push(line);
    }
  }
  flushAll();
  return doc;
}

// ── Save-time checks: warnings only, never a block ──────────────────────────

export function guidanceWarnings(doc: GuidanceDoc): string[] {
  const out: string[] = [];
  const levels = doc.folds.map((f) => f.level).filter((n): n is number => n !== null);
  if (levels.length) {
    const missing = [1, 2, 3, 4, 5].filter((n) => !levels.includes(n));
    if (missing.length) out.push(`Missing level heading: ${missing.join(", ")}`);
    const dup = [...new Set(levels.filter((n, i) => levels.indexOf(n) !== i))];
    if (dup.length) out.push(`Level ${dup.join(", ")} appears twice`);
  }
  const empty = doc.folds.filter((f) => f.blocks.length === 0).map((f) => f.title);
  if (empty.length) out.push(`Empty section: ${empty.join("; ")}`);
  return out;
}

// ── Compose: the legacy 5 level texts → one text in the Option B shape ──────

/** SOMA level names, 1-based. */
export const SOMA_LEVEL_NAMES = [
  "Fully supported",
  "Partially supported",
  "With prompting",
  "Mostly independent",
  "Independent",
];

interface LegacySection {
  heading: string;
  body: string;
}

/** One legacy level: drop `# title` + the positional subtitle line, then
 *  preamble + `## ` sections (heading's " - LEVEL n" suffix dropped). */
function legacyLevel(markdown: string): { preamble: string; sections: LegacySection[] } {
  const lines = markdown.replace(/\r\n?/g, "\n").split("\n");
  let i = 0;
  if (i < lines.length && lines[i].startsWith("# ")) {
    i += 1;
    if (i < lines.length && !lines[i].startsWith("## ")) i += 1;
  }
  const pre: string[] = [];
  while (i < lines.length && !lines[i].startsWith("## ")) pre.push(lines[i++]);
  const sections: LegacySection[] = [];
  while (i < lines.length) {
    const heading = lines[i++].slice(3).trim().replace(/\s+-\s+LEVEL\s+\d+.*$/i, "").trim();
    const body: string[] = [];
    while (i < lines.length && !lines[i].startsWith("## ")) body.push(lines[i++]);
    sections.push({ heading, body: body.join("\n").trim() });
  }
  return { preamble: pre.join("\n").trim(), sections };
}

/** "BEFORE YOU START" → "Before you start"; mixed case is left as written. */
function sentenceCase(s: string): string {
  if (s !== s.toUpperCase()) return s;
  const low = s.toLowerCase();
  return low.charAt(0).toUpperCase() + low.slice(1);
}

const LEGACY_SCALE = /^([1-5])\s*[–—-]\s*([^:]+):\s*(.+)$/;

/** A shared section's body; a "N – Name: text" score scale (3+ lines) becomes
 *  score lines 5→1 with every other line in the boxed note (the Option B look). */
function sharedBody(body: string): string {
  const lines = body.split("\n").map((l) => l.trim()).filter((l) => l !== "");
  const scale = lines.map((l) => l.match(LEGACY_SCALE));
  if (scale.filter(Boolean).length < 3) return body;
  const items = scale
    .filter((m): m is RegExpMatchArray => m !== null)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .map((m) => `- **${m[1]} ${m[2].trim()}** — ${m[3].trim()}`);
  const rest = lines.filter((_, i) => !scale[i]);
  const note = rest.length ? `\n\n${rest.map((l) => `> ${l}`).join("\n>\n")}` : "";
  return `${items.join("\n")}${note}`;
}

/**
 * Join 5 legacy level texts into one text. A `##` section present in all five
 * levels with the same body becomes a top fold (in level-1 order); each level
 * then becomes `# Level N · <label>` holding its own preamble + other sections.
 * Returns "" when every level is blank.
 */
export function composeFromLevels(levels: readonly string[], labels: readonly string[] = []): string {
  if (levels.length !== 5 || levels.every((t) => !(t ?? "").trim())) return "";
  const parsed = levels.map((t) => legacyLevel(t ?? ""));
  const bodyOf = (p: (typeof parsed)[number], h: string) => p.sections.find((s) => s.heading === h)?.body;
  const shared = parsed[0].sections
    .map((s) => s.heading)
    .filter((h, i, all) => all.indexOf(h) === i)
    .filter((h) => parsed.every((p) => bodyOf(p, h) !== undefined && bodyOf(p, h) === bodyOf(parsed[0], h)));

  const out: string[] = [];
  for (const h of shared) {
    const body = bodyOf(parsed[0], h) ?? "";
    out.push(`# ${sentenceCase(h)}`, ...(body ? [sharedBody(body)] : []), "");
  }
  parsed.forEach((p, i) => {
    const label = (labels[i] ?? "").trim() || SOMA_LEVEL_NAMES[i];
    out.push(`# Level ${i + 1} · ${label}`);
    if (p.preamble) out.push(p.preamble);
    for (const s of p.sections) {
      if (shared.includes(s.heading)) continue;
      out.push("", `## ${s.heading}`, ...(s.body ? [s.body] : []));
    }
    out.push("");
  });
  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim() + "\n";
}

/**
 * Did the librarian change a catalogue item's guidance from what they were
 * shown (its saved text, else its levels composed)? Only then is
 * `_carerGuidanceText` written — an untouched item keeps composing, so opening
 * or retitling an item never freezes a text onto it.
 */
export function guidanceTextChanged(edited: string, definition: Record<string, unknown> | null | undefined): boolean {
  return edited !== (resolveGuidanceText(null, definition) ?? "");
}

// ── Monitored events: the same one text (Ed 2026-09-29) ─────────────────────

/**
 * A monitored event's guidance (`_observationDefinition._monitoredEventGuidance`)
 * as a one-text guidance doc. The 57 texts loaded 2026-09-24 mark their sections
 * with `##` and never use `#`; read those `##` headings as folds. A text with any
 * `# ` line is already in the one-text rules and is returned as written (so an
 * author's own `##` labels stay labels). Absent/non-string → "".
 */
export function eventGuidanceText(stored: unknown): string {
  if (typeof stored !== "string") return "";
  const lines = stored.replace(/\r\n?/g, "\n").split("\n");
  if (lines.some((l) => FOLD.test(l.trim()))) return stored;
  return lines.map((l) => l.replace(/^(\s*)##(\s+)/, "$1#$2")).join("\n");
}

// ── Which text a carer sees ──────────────────────────────────────────────────

function text(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}

function fiveLevels(v: unknown): string[] | null {
  if (!Array.isArray(v) || v.length !== 5 || v.some((s) => typeof s !== "string")) return null;
  return v.every((s: string) => !s.trim()) ? null : (v as string[]);
}

/**
 * The guidance text for one goal, most specific first: the plan's whole-text
 * override, the plan's legacy 5-level override, the catalogue's text, the
 * catalogue's legacy 5 levels. null when there is none.
 */
export function resolveGuidanceText(
  goal: Record<string, unknown> | null | undefined,
  definition: Record<string, unknown> | null | undefined,
): string | null {
  const docs = Array.isArray(definition?.documentation) ? (definition!.documentation as unknown[]) : [];
  const labels = docs.map((d) => String((d as Record<string, unknown>)?.label ?? ""));
  const canonical = docs.map((d) => String((d as Record<string, unknown>)?.display ?? ""));
  const planLevels = fiveLevels(goal?._carerGuidance);
  return (
    text(goal?._carerGuidanceText) ??
    (planLevels ? text(composeFromLevels(planLevels, labels)) : null) ??
    text(definition?._carerGuidanceText) ??
    text(composeFromLevels(canonical.length === 5 ? canonical : [], labels))
  );
}
